// src/pages/Analytics.js - Added Export
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExportButton from '../components/ExportButton';

function Analytics() {
  const [analytics, setAnalytics] = useState({
    dailyRides: [],
    peakHours: [],
    vehicleUsage: [],
    revenueTrend: [],
    statusBreakdown: [],
    monthlyStats: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('week');

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const [statsRes, ridesRes] = await Promise.all([
        axios.get(`${API_URL}/stats`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }),
        axios.get(`${API_URL}/all-rides`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
      ]);
      
      const rides = ridesRes.data.rides || [];
      const stats = statsRes.data || {};
      
      const processed = processAnalyticsData(rides, stats);
      setAnalytics(processed);
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError(error.response?.data?.error || 'Failed to load analytics');
      setAnalytics({
        dailyRides: [],
        peakHours: [],
        vehicleUsage: [],
        revenueTrend: [],
        statusBreakdown: [],
        monthlyStats: {
          totalRides: 0,
          totalRevenue: 0,
          activeDrivers: 0,
          avgRating: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (rides, stats) => {
    const today = new Date();
    const days = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 90;
    
    const dailyRides = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const count = rides.filter(r => r.created_at?.startsWith(dateStr)).length;
      const revenue = rides
        .filter(r => r.created_at?.startsWith(dateStr) && r.status === 'completed')
        .reduce((sum, r) => sum + (r.total_fare || 0), 0);
      dailyRides.push({ date: dateStr, count, revenue });
    }

    const hourCount = {};
    rides.forEach(r => {
      if (r.created_at) {
        const hour = new Date(r.created_at).getHours();
        hourCount[hour] = (hourCount[hour] || 0) + 1;
      }
    });
    const peakHours = Object.entries(hourCount)
      .map(([hour, count]) => ({ hour: parseInt(hour), count }))
      .sort((a, b) => a.hour - b.hour);

    const vehicleCount = {};
    rides.forEach(r => {
      const v = r.vehicle_type || 'boda';
      vehicleCount[v] = (vehicleCount[v] || 0) + 1;
    });
    const vehicleUsage = Object.entries(vehicleCount).map(([type, count]) => ({
      type,
      count,
      label: type === 'boda' ? '🛵 Boda' :
             type === 'boda_electric' ? '⚡ Boda E' :
             type === 'car' ? '🚗 Car' : '🚙 Car XL'
    }));

    const statusCount = {};
    rides.forEach(r => {
      const s = r.status || 'pending';
      statusCount[s] = (statusCount[s] || 0) + 1;
    });
    const statusBreakdown = Object.entries(statusCount).map(([status, count]) => ({
      status,
      count,
      label: status === 'completed' ? '✅ Completed' :
             status === 'in_progress' ? '🔄 In Progress' :
             status === 'pending' ? '⏳ Pending' :
             status === 'cancelled' ? '❌ Cancelled' :
             status === 'accepted' ? '✓ Accepted' : status
    }));

    const completedRides = rides.filter(r => r.status === 'completed');
    const totalRevenue = completedRides.reduce((sum, r) => sum + (r.total_fare || 0), 0);
    const avgRating = completedRides.reduce((sum, r) => sum + (r.rating || 0), 0) / (completedRides.length || 1);

    return {
      dailyRides,
      peakHours,
      vehicleUsage,
      revenueTrend: dailyRides,
      statusBreakdown,
      monthlyStats: {
        totalRides: rides.length,
        completedRides: completedRides.length,
        totalRevenue: totalRevenue,
        avgRating: avgRating || 0,
        activeDrivers: stats.total_drivers || 0,
        totalCustomers: stats.total_customers || 0,
      }
    };
  };

  const getExportData = () => {
    return analytics.dailyRides.map(day => ({
      'Date': day.date,
      'Rides': day.count,
      'Revenue': day.revenue,
    }));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading analytics...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Analytics Dashboard</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#64748b' }}>
            <span>📊 {analytics.monthlyStats.totalRides || 0} total rides</span>
            <span style={{ color: '#10b981' }}>💰 UGX {(analytics.monthlyStats.totalRevenue || 0).toLocaleString()}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              padding: '10px 16px',
              borderRadius: '10px',
              border: '2px solid #e2e8f0',
              backgroundColor: 'white',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
          </select>
          <ExportButton 
            data={getExportData()} 
            filename={`analytics_${timeRange}`} 
            label="Export CSV"
          />
          <button
            onClick={fetchAnalytics}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '14px 18px',
          borderRadius: '10px',
          marginBottom: '16px',
          border: '1px solid #fecaca',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Total Rides</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f97316' }}>
            {analytics.monthlyStats.totalRides || 0}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Completed Rides</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>
            {analytics.monthlyStats.completedRides || 0}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Total Revenue</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f97316' }}>
            UGX {(analytics.monthlyStats.totalRevenue || 0).toLocaleString()}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Avg Rating</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b' }}>
            ⭐ {analytics.monthlyStats.avgRating ? analytics.monthlyStats.avgRating.toFixed(1) : '0'}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Active Drivers</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#3b82f6' }}>
            {analytics.monthlyStats.activeDrivers || 0}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Total Customers</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#8b5cf6' }}>
            {analytics.monthlyStats.totalCustomers || 0}
          </div>
        </div>
      </div>

      {/* Daily Rides Chart */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ marginTop: 0 }}>Daily Rides</h3>
        {analytics.dailyRides.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            No ride data available
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', height: '180px', padding: '10px 0' }}>
            {analytics.dailyRides.map((day, index) => {
              const max = Math.max(...analytics.dailyRides.map(d => d.count), 1);
              const height = (day.count / max * 100);
              return (
                <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: '100%',
                    height: `${Math.max(height, 4)}%`,
                    backgroundColor: '#f97316',
                    borderRadius: '4px',
                    minHeight: '4px',
                    transition: 'height 0.3s',
                  }} />
                  <div style={{ fontSize: '9px', marginTop: '4px', color: '#64748b' }}>
                    {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                  </div>
                  <div style={{ fontSize: '9px', fontWeight: 'bold' }}>{day.count}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Peak Hours */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Peak Hours</h3>
          {analytics.peakHours.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
              No data available
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '4px' }}>
              {[...Array(24)].map((_, hour) => {
                const data = analytics.peakHours.find(h => h.hour === hour);
                const count = data?.count || 0;
                const max = Math.max(...analytics.peakHours.map(h => h.count), 1);
                const intensity = (count / max * 100);
                return (
                  <div key={hour} style={{ textAlign: 'center' }}>
                    <div style={{
                      height: `${Math.max(intensity, 4)}%`,
                      backgroundColor: intensity > 50 ? '#f97316' : intensity > 25 ? '#fbbf24' : '#94a3b8',
                      borderRadius: '4px',
                      minHeight: '4px',
                      transition: 'height 0.3s',
                    }} />
                    <div style={{ fontSize: '8px', marginTop: '4px', color: '#64748b' }}>
                      {hour}h
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Vehicle Usage */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Vehicle Usage</h3>
          {analytics.vehicleUsage.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
              No data available
            </div>
          ) : (
            analytics.vehicleUsage.map((v) => {
              const total = analytics.vehicleUsage.reduce((sum, v) => sum + v.count, 0) || 1;
              const percentage = (v.count / total * 100).toFixed(1);
              return (
                <div key={v.type} style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span>{v.label || v.type}</span>
                    <span>{percentage}% ({v.count})</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${percentage}%`,
                      height: '100%',
                      backgroundColor: v.type === 'boda' ? '#f97316' : 
                                     v.type === 'boda_electric' ? '#10b981' :
                                     v.type === 'car' ? '#3b82f6' : '#8b5cf6',
                      borderRadius: '4px',
                    }} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Status Breakdown */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '24px',
      }}>
        <h3 style={{ marginTop: 0 }}>Ride Status Breakdown</h3>
        {analytics.statusBreakdown.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
            No data available
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
            {analytics.statusBreakdown.map((item) => {
              const total = analytics.statusBreakdown.reduce((sum, s) => sum + s.count, 0) || 1;
              const percentage = (item.count / total * 100).toFixed(1);
              const colors = {
                completed: '#10b981',
                in_progress: '#f59e0b',
                pending: '#f59e0b',
                cancelled: '#ef4444',
                accepted: '#3b82f6',
              };
              return (
                <div key={item.status} style={{
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: `2px solid ${colors[item.status] || '#e2e8f0'}`,
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: colors[item.status] || '#64748b' }}>
                    {item.count}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>{item.label || item.status}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{percentage}%</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Revenue Chart */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ marginTop: 0 }}>Revenue Trend</h3>
        {analytics.revenueTrend.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
            No revenue data available
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', height: '150px', padding: '10px 0' }}>
            {analytics.revenueTrend.map((day, index) => {
              const max = Math.max(...analytics.revenueTrend.map(d => d.revenue), 1);
              const height = (day.revenue / max * 100);
              return (
                <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: '100%',
                    height: `${Math.max(height, 4)}%`,
                    backgroundColor: '#10b981',
                    borderRadius: '4px',
                    minHeight: '4px',
                    transition: 'height 0.3s',
                  }} />
                  <div style={{ fontSize: '9px', marginTop: '4px', color: '#64748b' }}>
                    {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                  </div>
                  <div style={{ fontSize: '8px', fontWeight: 'bold', color: '#10b981' }}>
                    {day.revenue > 0 ? `${(day.revenue/1000).toFixed(0)}k` : '0'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {analytics.revenueTrend.length > 0 && (
          <div style={{ textAlign: 'center', fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
            Total Revenue: UGX {analytics.revenueTrend.reduce((sum, d) => sum + d.revenue, 0).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}

export default Analytics;